import {
    OrderByInput,
    Product,
    useProductListOrder,
    useSetProductListOrder,
} from '@gripp/shared-logic';
import { InfiniteDataGrid, ProductAlertModal } from '@gripp/shared-ui';
import { useState } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { CreateProductButton } from './createProductButton';
import { useColumnsProduct } from './useColumnsProduct';
import { ProductAlertButton } from './productAlertButton';

type ProductsListProps = {
  filter: any;
  showCheckboxColumn: boolean;
  onProductClick: (product: Product) => void;
};

export const ProductsList = ({
  filter,
  showCheckboxColumn,
  onProductClick,
}: ProductsListProps) => {
  const { containerMaxHeight, listMaxHeight } = useMaxHeight();
  const { width } = useWindowDimensions();

  const { data: productOrder } = useProductListOrder();
  const { mutateAsync: setProductOrder } = useSetProductListOrder();

  const columns = useColumnsProduct(showCheckboxColumn);

  const [productsSelected, setProductsSelected] = useState<
    string[] | undefined
  >(undefined);

  const [showAlertModal, setShowAlertModal] = useState(false);
  const showAlertButton = productsSelected?.length === 1;

  const updateProductSelection = (rows: any) => {
    setProductsSelected(rows);
  };

  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setProductOrder(orderBy);
  };

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <View style={[styles.textWrapper, { maxWidth: width - 10 }]}>
        <Text style={styles.emptyTapText} numberOfLines={2}>
          Tap '+ Create Product' to add your first product.
        </Text>
      </View>
    </View>
  );

  const renderHeaderContent = () => {
    if (showAlertButton && productsSelected?.length === 1) {
      return (
        <ProductAlertButton
          onPress={() => setShowAlertModal(true)}
        />
      );
    }
    return productsSelected?.length && productsSelected?.length > 0 ? (
      <></>
    ) : (
      <CreateProductButton />
    );
  };

  return (
    <View
      style={[
        styles.productsContainer,
        {
          maxHeight: containerMaxHeight,
        },
      ]}
    >
      <InfiniteDataGrid
        columns={columns as any}
        query={LIST_PRODUCTS}
        dataPrefix="product.listProducts"
        modelName="product"
        orderBy={productOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        listMaxHeight={listMaxHeight}
        showScrollIndicator={true}
        onOrderByChange={onOrderByChange}
        textEmpty={renderEmpty()}
        showEmptyTable
        updateRowsSelected={(rows) => updateProductSelection(rows)}
        headerContent={renderHeaderContent()}
        onRowClick={onProductClick}
        exportFileName="products-export"
      />
      {showAlertModal && productsSelected?.[0] && (
        <ProductAlertModal
          visible={showAlertModal}
          onClose={() => setShowAlertModal(false)}
          productId={productsSelected[0]}
        />
      )}
    </View>
  );
};

const LIST_PRODUCTS = `
  query FindProducts($filter: ListProductsFilter, $page: PageRequest, $orderBy: OrderByInput) {
    product {
      listProducts(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          name
          description
          assetsCount
          filesCount
          addonsCount
          routines {
            id
            name
          }
          alerts
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  productsContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  emptyContainer: {
    flexDirection: 'column',
    rowGap: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textWrapper: {
    width: '100%',
  },
  emptyTapText: {
    fontWeight: '700',
    fontSize: 22,
    textAlign: 'center',
  },
});
